/* ═══════════════════════════════════════════════════════════════
   Utils — Shared helper functions used across components
   Depends on: constants.ts (loaded first)
   ═══════════════════════════════════════════════════════════════ */

import { CSI_NUMBER_FORMATS, ALL_TOKENS, SC, PPI } from './constants';
import {
  isNumberedPlaceholder,
  formatFormaDate,
  ISSUANCE_DATE_ID,
} from '../shared/forma-placeholders';
import { applyCaseMod as sharedApplyCaseMod } from '../shared/zone-layout';
import type {
  LevelStyle,
  HeaderFooter,
  DocSettings,
  SpecialSettings,
  NumberFormatOption,
} from '../shared/types';

export function inPx(i: number): number {
  return i * PPI * SC;
}
export function pxIn(p: number): number {
  return p / (PPI * SC);
}
export function ptPx(p: number): number {
  return (p / 72) * PPI * SC;
}

export function snapCoarse(v: number): number {
  return Math.round(v / 0.25) * 0.25;
}
export function snapFine(v: number): number {
  return Math.round(v / 0.0625) * 0.0625;
}
export function snap(v: number, fine?: boolean): number {
  return fine ? snapFine(v) : snapCoarse(v);
}

export function defaultNumberFormats(): string[] {
  return CSI_NUMBER_FORMATS.map((l) => l.options[0].id);
}

export function getFormatInfo(levelIdx: number, fmtId: string): NumberFormatOption | null {
  const lvl = CSI_NUMBER_FORMATS[levelIdx];
  if (!lvl) return null;
  return lvl.options.find((o) => o.id === fmtId) || lvl.options[0];
}

export function getSamples(numFormats?: string[]): Array<Array<{ num: string; text: string }>> {
  const fmts = numFormats || defaultNumberFormats();
  const sampleNums: Array<(f: string) => string[]> = [
    (f) => (f === 'part_space' ? ['PART 1', 'PART 2'] : ['PART 1 -', 'PART 2 -']),
    (f) => (f === '1.1' ? ['1.1', '1.2'] : ['1.01', '1.02']),
    () => ['A.', 'B.'],
    () => ['1.', '2.'],
    () => ['a.', 'b.'],
    (f) => {
      const info = getFormatInfo(5, f);
      return info && info.id === '(1)' ? ['(1)', '(2)'] : ['1)', '2)'];
    },
    (f) => {
      const info = getFormatInfo(6, f);
      return info && info.id === '(a)' ? ['(a)', '(b)'] : ['a)', 'b)'];
    },
    (f) => {
      const info = getFormatInfo(7, f);
      return info && info.id === '1)' ? ['1)', '2)'] : ['(1)', '(2)'];
    },
    (f) => {
      const info = getFormatInfo(8, f);
      return info && info.id === 'a)' ? ['a)', 'b)'] : ['(a)', '(b)'];
    },
  ];
  // Placeholder text — a forma is a TEMPLATE, so the canvas shows the numbering
  // scheme + neutral filler (never real spec content), letting the user focus on
  // format, not words. The numbering already conveys which level each row is.
  const sampleTexts = [
    [
      'This is what your level 1 text will look like',
      'This is what your level 1 text will look like',
    ],
    [
      'This is what your level 2 text will look like',
      'This is what your level 2 text will look like',
    ],
    [
      'This is what your level 3 text will look like',
      'This is what your level 3 text will look like',
    ],
    [
      'This is what your level 4 text will look like',
      'This is what your level 4 text will look like',
    ],
    [
      'This is what your level 5 text will look like',
      'This is what your level 5 text will look like',
    ],
    [
      'This is what your level 6 text will look like',
      'This is what your level 6 text will look like',
    ],
    [
      'This is what your level 7 text will look like',
      'This is what your level 7 text will look like',
    ],
    [
      'This is what your level 8 text will look like',
      'This is what your level 8 text will look like',
    ],
    [
      'This is what your level 9 text will look like',
      'This is what your level 9 text will look like',
    ],
  ];
  return sampleNums.map((fn, i) => {
    const nums = fn(fmts[i]);
    const texts = sampleTexts[i];
    return [
      { num: nums[0], text: texts[0] },
      { num: nums[1], text: texts[1] },
    ];
  });
}

export type TextCase = 'none' | 'upper' | 'title' | 'sentence';
/** Effective case for a level — textCase wins, else legacy allCaps. */
export function effectiveCase(lvl: {
  textCase?: 'upper' | 'title' | 'sentence';
  allCaps?: boolean;
}): TextCase {
  if (lvl.textCase) return lvl.textCase;
  return lvl.allCaps ? 'upper' : 'none';
}
/** Apply a text case to a string (for the designer preview + spec compile). */
export function applyTextCase(text: string, c: TextCase): string {
  switch (c) {
    case 'upper':
      return text.toUpperCase();
    case 'title':
      return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence':
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    default:
      return text;
  }
}

export function mkLevels(): LevelStyle[] {
  return [
    {
      level: 1,
      bold: true,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 0,
      hangingIndent: 0.35,
      spaceBefore: 12,
      spaceAfter: 12,
      keepWithNext: true,
      keepLinesTogether: true,
      noSpaceSameStyle: false,
    },
    {
      level: 2,
      bold: false,
      allCaps: true,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 0,
      hangingIndent: 0.6,
      spaceBefore: 0,
      spaceAfter: 12,
      keepWithNext: true,
      keepLinesTogether: true,
      noSpaceSameStyle: true,
    },
    {
      level: 3,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 0.19,
      hangingIndent: 0.31,
      spaceBefore: 0,
      spaceAfter: 6,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: false,
    },
    {
      level: 4,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 0.5,
      hangingIndent: 0.31,
      spaceBefore: 12,
      spaceAfter: 12,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: true,
    },
    {
      level: 5,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 0.81,
      hangingIndent: 0.32,
      spaceBefore: 0,
      spaceAfter: 12,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: true,
    },
    {
      level: 6,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 1.19,
      hangingIndent: 0.31,
      spaceBefore: 0,
      spaceAfter: 12,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: true,
    },
    {
      level: 7,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 1.5,
      hangingIndent: 0.25,
      spaceBefore: 0,
      spaceAfter: 12,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: true,
    },
    {
      level: 8,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 1.75,
      hangingIndent: 0.25,
      spaceBefore: 0,
      spaceAfter: 12,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: true,
    },
    {
      level: 9,
      bold: false,
      allCaps: false,
      italic: false,
      underline: false,
      fontColor: '000000',
      indent: 2,
      hangingIndent: 0.25,
      spaceBefore: 2,
      spaceAfter: 0,
      keepWithNext: false,
      keepLinesTogether: false,
      noSpaceSameStyle: false,
    },
  ];
}

export function mkHF(): HeaderFooter {
  return {
    headerFirstLeft: [],
    headerFirstCenter: [],
    headerFirstRight: [],
    headerOddLeft: ['{SpecNumber}'],
    headerOddCenter: [],
    headerOddRight: ['{SpecName}'],
    headerEvenLeft: ['{SpecName}'],
    headerEvenCenter: [],
    headerEvenRight: ['{SpecNumber}'],
    footerFirstLeft: [],
    footerFirstCenter: [],
    footerFirstRight: [],
    footerOddLeft: ['{Date}'],
    footerOddCenter: [],
    footerOddRight: ['{PageOfPages}'],
    footerEvenLeft: ['{PageOfPages}'],
    footerEvenCenter: [],
    footerEvenRight: ['{Date}'],
  };
}

export function mkDoc(): DocSettings {
  return {
    standardName: '',
    clientName: '',
    version: '1.0',
    bodyFont: 'Times New Roman',
    bodyFontSize: 12,
    pageWidth: 8.5,
    pageHeight: 11,
    marginTop: 1,
    marginBottom: 1,
    marginLeft: 1,
    marginRight: 1,
    firstPageDifferent: false,
    oddEvenDifferent: false,
    lineSpacing: 1,
    widowOrphan: true,
    specNumberFormat: '## ## ##',
  };
}

export function mkSp(): SpecialSettings {
  return {
    endOfSectionText: 'END OF SECTION',
    eosEnabled: true,
    eosSpacing: 24,
    blankPageEnabled: false,
    intentionallyBlankText: 'THIS PAGE INTENTIONALLY LEFT BLANK',
    blankPageCondition: 'always',
    blankPageJustify: 'center',
    blankPageVertical: 50,
    numberFormats: defaultNumberFormats(),
    bodyTopLeft: [],
    bodyTopCenter: [],
    bodyTopRight: [],
    postLeft: [],
    postCenter: [],
    postRight: [],
  };
}

/** Check if a modifier candidate is valid (bold, caps, titlecase, sentencecase,
 *  italic, underline, color:RRGGBB, size:N) */
export function isValidMod(m: string): boolean {
  if (['bold', 'caps', 'titlecase', 'sentencecase', 'italic', 'underline'].includes(m)) return true;
  if (m.startsWith('color:') && /^[0-9a-fA-F]{6}$/.test(m.slice(6))) return true;
  // Vertical spacing around a token, in points: sb:N (before) / sa:N (after).
  // Lets headers/footers/pre-post add gaps without hard-coding blank returns.
  if (/^s[ab]:\d+(\.\d+)?$/.test(m)) return true;
  // Contract §3.3 — per-token font size override, in points (zone base size
  // applies when absent). "size:" never collides with "sb:"/"sa:" above.
  if (/^size:\d+(\.\d+)?$/.test(m)) return true;
  return false;
}

/* ─── Pure token-value transforms — shared so ONE token or MANY selected tokens
       (across any sections) can be edited the same way. Given a token value
       string, return a new value string. No React, no side effects. ─── */

/** Parse a token's mod suffix (bold/italic/caps/underline/color:/sb:/sa:) → Set. */
export function parseTokenMods(v: string): Set<string> {
  const mods = new Set<string>();
  if (!v) return mods;
  const pipeIdx = v.lastIndexOf('|');
  if (pipeIdx <= 0) return mods;
  let modStr = v.slice(pipeIdx + 1);
  if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
  const cands = modStr
    .toLowerCase()
    .split(',')
    .map((s) => s.trim());
  if (cands.every((m) => isValidMod(m))) cands.forEach((m) => mods.add(m));
  return mods;
}

/** Strip a valid mod suffix off a token value, leaving the base. */
export function stripTokenBase(v: string): string {
  const pipeIdx = v.lastIndexOf('|');
  if (pipeIdx <= 0) return v;
  let testStr = v.slice(pipeIdx + 1);
  if (testStr.endsWith('}')) testStr = testStr.slice(0, -1);
  if (
    testStr
      .toLowerCase()
      .split(',')
      .map((s) => s.trim())
      .every((m) => isValidMod(m))
  ) {
    return v.slice(0, pipeIdx) + (v.endsWith('}') ? '}' : '');
  }
  return v;
}

/** Recombine a base value + mods into a token value string. */
export function rebuildTokenValue(base: string, mods: Set<string>): string {
  if (mods.size === 0) return base;
  const modStr = [...mods].join(',');
  return base.endsWith('}') ? base.slice(0, -1) + '|' + modStr + '}' : base + '|' + modStr;
}

/** Toggle a boolean mod (bold/italic/caps/underline) on a token value. */
export function toggleTokenMod(v: string, mod: string): string {
  const mods = parseTokenMods(v);
  if (mods.has(mod)) mods.delete(mod);
  else mods.add(mod);
  return rebuildTokenValue(stripTokenBase(v), mods);
}

/** Set (or clear with null) the mutually-exclusive text-case mod:
 *  'caps' | 'titlecase' | 'sentencecase' | null. A token prints in exactly
 *  ONE case transform, never two — setting one clears the other two. */
export function setCaseMod(v: string, mode: 'caps' | 'titlecase' | 'sentencecase' | null): string {
  const mods = parseTokenMods(v);
  mods.delete('caps');
  mods.delete('titlecase');
  mods.delete('sentencecase');
  if (mode) mods.add(mode);
  return rebuildTokenValue(stripTokenBase(v), mods);
}

/** Apply a token's case mod to already-resolved display text — the ONE spot
 *  every preview (Studio chip, Designer paper, "real preview") transforms
 *  text, so what you see while editing is what the .docx prints. `caps` is
 *  ALSO expressed as CSS `text-transform:uppercase` (tokModStyle) for the
 *  chip/label styling paths that only have a style object to hand, but this
 *  is the one that's correct for `titlecase`/`sentencecase` — CSS has no
 *  sentence-case value, and `text-transform:capitalize` doesn't lowercase
 *  already-uppercase source text, so both need a real string transform. */
export function applyCaseModText(text: string, v: string): string {
  return sharedApplyCaseMod(text, parseTokenMods(v));
}

/** Set (or clear with null) the color mod on a token value. */
export function setTokenColor(v: string, hex: string | null): string {
  const mods = parseTokenMods(v);
  for (const m of [...mods]) if (m.startsWith('color:')) mods.delete(m);
  if (hex) mods.add('color:' + hex.replace('#', '').toLowerCase());
  return rebuildTokenValue(stripTokenBase(v), mods);
}

/** Set before/after spacing (pt) on a token value (0 clears). */
export function setTokenSpace(v: string, which: 'sb' | 'sa', pt: number): string {
  const mods = parseTokenMods(v);
  for (const m of [...mods]) if (m.startsWith(which + ':')) mods.delete(m);
  if (pt > 0) mods.add(which + ':' + pt);
  return rebuildTokenValue(stripTokenBase(v), mods);
}

/** Read one spacing edge (pt) from a token value. */
export function getTokenSpace(v: string, which: 'sb' | 'sa'): number {
  for (const m of parseTokenMods(v))
    if (m.startsWith(which + ':')) return parseFloat(m.slice(3)) || 0;
  return 0;
}

/** Set (or clear with null/0) the per-token font-size override, in points —
 *  contract §3 R6 item 3 ("mixed formatting inside zones"): a header/footer
 *  line can mix a large firm name with a small suffix. Absent → the zone's
 *  base size applies (tokModStyle already reads this, see `sizeMod` there). */
export function setTokenSize(v: string, pt: number | null): string {
  const mods = parseTokenMods(v);
  for (const m of [...mods]) if (m.startsWith('size:')) mods.delete(m);
  if (pt && pt > 0) mods.add('size:' + pt);
  return rebuildTokenValue(stripTokenBase(v), mods);
}

/** Read the per-token font-size override (pt), or null when unset. */
export function getTokenSize(v: string): number | null {
  for (const m of parseTokenMods(v)) {
    if (m.startsWith('size:')) return parseFloat(m.slice(5)) || null;
  }
  return null;
}

/** Read the color mod (hex without #) from a token value, or null. */
export function getTokenColor(v: string): string | null {
  for (const m of parseTokenMods(v)) if (m.startsWith('color:')) return m.slice(6);
  return null;
}

/** Parse a token's spacing mods → { before, after } in points (0 if none). */
export function tokSpacing(v: string): { before: number; after: number } {
  const out = { before: 0, after: 0 };
  if (!v || v === '\n') return out;
  const pipeIdx = v.lastIndexOf('|');
  if (pipeIdx <= 0) return out;
  let modStr = v.slice(pipeIdx + 1);
  if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
  const cands = modStr
    .toLowerCase()
    .split(',')
    .map((x) => x.trim());
  if (!cands.every((m) => isValidMod(m))) return out; // suffix isn't valid mods
  for (const m of cands) {
    if (m.startsWith('sb:')) out.before = parseFloat(m.slice(3)) || 0;
    else if (m.startsWith('sa:')) out.after = parseFloat(m.slice(3)) || 0;
  }
  return out;
}

export function tokBase(v: string): string {
  if (!v) return v;
  // Image tokens: {Image:id} or {Image:id|mods} → base is {Image:id}
  if (v.startsWith('{Image:')) {
    const closeIdx = v.indexOf('}');
    if (closeIdx < 0) return v;
    const inner = v.slice(7, closeIdx); // everything after {Image:
    const pipeIdx = inner.indexOf('|');
    if (pipeIdx < 0) return `{Image:${inner}}`;
    return `{Image:${inner.slice(0, pipeIdx)}}`;
  }
  // Line tokens: {Line} or {Line|thickness,color}
  if (v.startsWith('{Line')) return '{Line}';
  const pi = v.lastIndexOf('|');
  if (pi <= 0) return v;
  let modStr = v.slice(pi + 1);
  if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
  const cands = modStr
    .toLowerCase()
    .split(',')
    .map((s) => s.trim());
  if (cands.every((m) => isValidMod(m))) return v.slice(0, pi) + (v.endsWith('}') ? '}' : '');
  return v;
}

// \u2500\u2500\u2500 Project Tokens (user-defined, per project) \u2500\u2500\u2500
// Chip labels for {ProjectToken:id} need the open project's token names.
// A module registry avoids threading project context through every tokLabel
// call site; the designer/preview set it when a project forma is open.
let projectTokenLabels: Record<string, string> = {};
export function setProjectTokenLabels(map: Record<string, string>): void {
  projectTokenLabels = map || {};
}
export function projectTokenLabel(id: string): string | undefined {
  return projectTokenLabels[id];
}
// Resolved CONTENT for {ProjectToken:id} chips — set alongside the labels when
// a project forma is open, so the designer canvas previews the real line
// ("RWB 26055-00 – Issuance 2") instead of just the token's name.
let projectTokenContents: Record<string, string> = {};
export function setProjectTokenContents(map: Record<string, string>): void {
  projectTokenContents = map || {};
}

// ─── Project placeholders ({P1}…, {IssuanceDate}) ───
// Two registries, same pattern as above: what each blank RESOLVES to in the
// open project forma (empty for a master), and what the imported template
// HAD there. The canvas shows the resolved value when there is one, the
// memory otherwise — so a master never renders a bare "{P4}".
let placeholderValues: Record<string, string> = {};
let placeholderMemories: Record<string, string> = {};
// True while a PROJECT copy is open: only then is "nobody has bound this yet"
// a problem worth flagging — in a master, an unset blank is the whole point.
let placeholderProjectMode = false;
export function setPlaceholderProjectMode(on: boolean): void {
  placeholderProjectMode = !!on;
}
export function isProjectForma(): boolean {
  return placeholderProjectMode;
}
export function setPlaceholderValues(map: Record<string, string>): void {
  placeholderValues = map || {};
}
export function setPlaceholderMemories(map: Record<string, string>): void {
  placeholderMemories = map || {};
}
export function placeholderMemory(id: string): string | undefined {
  return placeholderMemories[id];
}
export function placeholderValue(id: string): string | undefined {
  return placeholderValues[id];
}

export function tokLabel(v: string): string {
  if (!v) return '';
  if (v === '\n') return '\u21b5';
  const b = tokBase(v);
  if (b.startsWith('{Image:')) return '\ud83d\uddbc Image';
  if (b.startsWith('{ProjectToken:')) {
    const id = b.slice('{ProjectToken:'.length, -1);
    return projectTokenLabels[id] || 'Project Token';
  }
  if (isNumberedPlaceholder(b)) return b.slice(1, -1); // "{P4}" → "P4"
  const t = ALL_TOKENS.find((x) => x.value === b);
  return t ? t.label : v.trim().length === 0 ? '[ ]' : '"' + v + '"';
}

export function tokColor(v: string): string {
  if (v === '\n') return '#999';
  const b = tokBase(v);
  if (b.startsWith('{Image:')) return '#2a6a8a';
  // Placeholders belong to the project-linked family (same slate blue).
  if (b.startsWith('{ProjectToken:') || isNumberedPlaceholder(b)) return '#46627f';
  const t = ALL_TOKENS.find((x) => x.value === b);
  return t ? t.color : '#666';
}

export function tokPreview(v: string, special?: any, doc?: any): string | null {
  if (!v) return '';
  if (v === '\n') return null;
  const b = tokBase(v);
  const specFmt = doc?.specNumberFormat || '## ## ##';
  let digitIdx = 0;
  const digits = '000000';
  const specPreview = specFmt.replace(/#/g, () => digits[digitIdx++ % digits.length]);
  switch (b) {
    case '{SpecNumber}':
      return specPreview;
    case '{SpecName}':
      return 'SPECIFICATION NAME';
    case '{SpecNumName}':
      return specPreview + ' - SPECIFICATION NAME';
    case '{Page}':
      return '1';
    case '{NumPages}':
      return '4';
    case '{PageOfPages}':
      return 'Page 1 of 4';
    case '{Date}':
      return new Date().toLocaleDateString('en-US');
    case '{EndOfSection}':
      return (special && special.endOfSectionText) || 'END OF SECTION';
    case '{Space}':
      return '\u00A0';
    case '{Line}':
      return '__LINE__';
    // Project-linked tokens \u2014 sample values in the designer; the word engine
    // fills the real values from the project at export time.
    case '{Project.Name}':
      return 'PROJECT NAME';
    case '{Project.ClientProjectName}':
      return 'CLIENT PROJECT NAME';
    case '{Project.Number}':
      return 'PROJECT NUMBER';
    case '{Project.Address}':
      return 'PROJECT ADDRESS';
    case '{Client.Name}':
      return 'CLIENT NAME';
    case '{Deliverable}':
      return 'DELIVERABLE';
    case '{Issuance.Name}':
      return 'ISSUANCE';
    case '{Issuance.Date}':
      return 'ISSUANCE DATE';
    case '{IssuanceDate}':
      // Bound in a project ⇒ the real issuance date; in a master ⇒ a sample
      // written in this forma's own date format, so the designer shows the
      // shape the client will actually get.
      return (
        placeholderValues[ISSUANCE_DATE_ID] ||
        formatFormaDate(new Date(), doc?.dateFormat) ||
        'ISSUANCE DATE'
      );
    default: {
      if (b.startsWith('{Image:')) return '__IMAGE__';
      if (isNumberedPlaceholder(b)) {
        // Resolved value (project copy) → the template's memory (master) →
        // the bare id, which only shows for a blank nobody has explained yet.
        const id = b.slice(1, -1);
        return placeholderValues[id] || placeholderMemories[id] || id;
      }
      if (b.startsWith('{ProjectToken:')) {
        // Real resolved content when a project forma is open (registry set by
        // app.tsx); null otherwise so the chip falls back to the token's name.
        const id = b.slice('{ProjectToken:'.length, -1);
        const content = projectTokenContents[id];
        return content && content.trim() ? content : null;
      }

      let text = v;
      const pipeIdx = text.lastIndexOf('|');
      if (pipeIdx > 0) {
        let ms = text.slice(pipeIdx + 1);
        if (ms.endsWith('}')) ms = ms.slice(0, -1);
        const cands2 = ms
          .toLowerCase()
          .split(',')
          .map((s) => s.trim());
        if (cands2.every((m) => isValidMod(m)))
          text = text.slice(0, pipeIdx) + (v.endsWith('}') ? '}' : '');
      }
      if (text.startsWith('{') && text.endsWith('}')) text = text.slice(1, -1);
      return text;
    }
  }
}

export function tokModStyle(v: string): Record<string, string> {
  const s: Record<string, string> = {};
  if (!v) return s;
  const pipeIdx = v.lastIndexOf('|');
  if (pipeIdx <= 0) return s;
  let modStr = v.slice(pipeIdx + 1);
  if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
  const cands = modStr
    .toLowerCase()
    .split(',')
    .map((x) => x.trim());
  if (!cands.every((m) => isValidMod(m))) return s;
  if (cands.includes('bold')) s.fontWeight = 'bold';
  if (cands.includes('italic')) s.fontStyle = 'italic';
  if (cands.includes('caps')) s.textTransform = 'uppercase';
  if (cands.includes('underline')) s.textDecoration = 'underline';
  const colorMod = cands.find((m) => m.startsWith('color:'));
  if (colorMod) s.color = '#' + colorMod.slice(6);
  // Contract §3.3 — a token's size:N mod prints at that point size, same as
  // the .docx run (word-engine's engineApplySizeMod). Absolute pt is exact:
  // the paper's other pt→px conversions (shared/body-layout's ptToPx) use
  // the identical 96/72 CSS reference, so this needs no separate scaling.
  const sizeMod = cands.find((m) => m.startsWith('size:'));
  if (sizeMod) {
    const pt = parseFloat(sizeMod.slice(5));
    if (pt > 0) s.fontSize = pt + 'pt';
  }
  return s;
}

/** Parse {Line|thickness,color} → { thickness: number (pt), color: string (hex) } */
export function parseLineParams(v: string): { thickness: number; color: string } {
  const defaults = { thickness: 1, color: '000000' };
  if (!v || !v.startsWith('{Line')) return defaults;
  const inner = v.slice(5, v.endsWith('}') ? -1 : undefined);
  if (!inner || inner[0] !== '|') return defaults;
  const parts = inner
    .slice(1)
    .split(',')
    .map((s) => s.trim());
  const t = parseFloat(parts[0]);
  if (!isNaN(t)) defaults.thickness = Math.max(0.25, Math.min(6, t));
  if (parts[1]) defaults.color = parts[1].replace('#', '');
  return defaults;
}

/** Extract image ID from {Image:someId} token */
export function parseImageId(v: string): string | null {
  if (!v || !v.startsWith('{Image:')) return null;
  const close = v.indexOf('}');
  if (close < 0) return null;
  const inner = v.slice(7, close);
  const pipe = inner.indexOf('|');
  return pipe < 0 ? inner : inner.slice(0, pipe);
}

/**
 * Normalize token array for WYSIWYG preview — splits custom text tokens
 * containing embedded \n into separate tokens with \n between them.
 * Preserves modifiers on all parts.
 * Mirrors what the word engine's normalizeTokens does.
 */
export function normalizeForPreview(tokens: string[]): string[] {
  if (!tokens || tokens.length === 0) return [];
  const result: string[] = [];
  for (const t of tokens) {
    if (t === '\n') {
      result.push(t);
      continue;
    }
    const base = tokBase(t);
    if (base.startsWith('{') && base.endsWith('}')) {
      result.push(t);
      continue;
    }
    if (!t.includes('\n')) {
      result.push(t);
      continue;
    }
    // Custom text with embedded newlines — preserve modifier suffix on all parts
    const pipeIdx = t.lastIndexOf('|');
    let modSuffix = '';
    if (pipeIdx > 0) {
      const ms = t.slice(pipeIdx + 1);
      const cands = ms
        .toLowerCase()
        .split(',')
        .map((s) => s.trim());
      if (cands.every((m) => isValidMod(m))) modSuffix = t.slice(pipeIdx);
    }
    const textPart = modSuffix ? t.slice(0, pipeIdx) : t;
    const parts = textPart.split('\n');
    parts.forEach((part, i) => {
      if (part.length > 0) result.push(part + modSuffix);
      if (i < parts.length - 1) result.push('\n');
    });
  }
  return result;
}

// ── Expose as globals for legacy JSX files ──
(window as any).inPx = inPx;
(window as any).pxIn = pxIn;
(window as any).ptPx = ptPx;
(window as any).snapCoarse = snapCoarse;
(window as any).snapFine = snapFine;
(window as any).snap = snap;
(window as any).defaultNumberFormats = defaultNumberFormats;
(window as any).getFormatInfo = getFormatInfo;
(window as any).getSamples = getSamples;
(window as any).mkLevels = mkLevels;
(window as any).mkHF = mkHF;
(window as any).mkDoc = mkDoc;
(window as any).mkSp = mkSp;
(window as any).tokBase = tokBase;
(window as any).tokLabel = tokLabel;
(window as any).tokColor = tokColor;
(window as any).tokPreview = tokPreview;
(window as any).tokModStyle = tokModStyle;
